/**
 * Real Tree-sitter integration using WASM modules
 * Replaces simulation with actual parsing for Rust and C
 */

import { Parser, Language } from 'web-tree-sitter';
import { ASTToken, ASTLine, ParseResult } from './treeSitterParser';

// Tree-sitter grammar modules
let RustLanguage: Language | null = null;
let CLanguage: Language | null = null;
let GoLanguage: Language | null = null;
let parser: Parser | null = null;
let initializationPromise: Promise<boolean> | null = null;

/**
 * Initialize Tree-sitter with required languages
 */
export async function initializeTreeSitter(): Promise<boolean> {
  // Return existing initialization if already in progress
  if (initializationPromise) {
    return initializationPromise;
  }
  
  // Start new initialization
  initializationPromise = _doInitialization();
  return initializationPromise;
}

async function _doInitialization(): Promise<boolean> {
  try {
    console.log('🌳 Initializing Tree-sitter...');
    
    // Initialize parser with correct WASM path
    await Parser.init({
      locateFile(scriptName: string, scriptDirectory: string) {
        if (scriptName === 'tree-sitter.wasm') {
          return '/tree-sitter.wasm';
        }
        return scriptDirectory + scriptName;
      }
    });
    parser = new Parser();
    console.log('✅ Tree-sitter parser created');
    
    // Load language grammars
    console.log('🌳 Loading Tree-sitter grammars...');
    
    // Load Rust grammar
    try {
      RustLanguage = await Language.load('/tree-sitter-rust.wasm');
      console.log('✅ Rust grammar loaded');
    } catch (error) {
      console.warn('⚠️ Failed to load Rust grammar:', error);
    }
    
    // Load C grammar  
    try {
      CLanguage = await Language.load('/tree-sitter-c.wasm');
      console.log('✅ C grammar loaded');
    } catch (error) {
      console.warn('⚠️ Failed to load C grammar:', error);
    }
    
    // Load Go grammar
    try {
      GoLanguage = await Language.load('/tree-sitter-go.wasm');
      console.log('✅ Go grammar loaded');
    } catch (error) {
      console.warn('⚠️ Failed to load Go grammar:', error);
    }
    
    const hasLanguages = RustLanguage !== null || CLanguage !== null || GoLanguage !== null;
    console.log(`✅ Tree-sitter initialization ${hasLanguages ? 'successful' : 'failed'}: Rust=${!!RustLanguage}, C=${!!CLanguage}, Go=${!!GoLanguage}`);
    return hasLanguages;
    
  } catch (error) {
    console.error('❌ Failed to initialize Tree-sitter:', error);
    return false;
  }
}

/**
 * Check if Tree-sitter is available for a language
 */
export async function isTreeSitterAvailable(language: string): Promise<boolean> {
  const lang = language.toLowerCase();
  
  // Ensure Tree-sitter is initialized
  if (!initializationPromise) {
    console.log('🔄 Tree-sitter not initialized, starting initialization...');
    await initializeTreeSitter();
  } else {
    // Wait for any ongoing initialization
    await initializationPromise;
  }
  
  const available = (lang === 'rust' && RustLanguage !== null) || 
                   (lang === 'c' && CLanguage !== null) ||
                   (lang === 'go' && GoLanguage !== null);
  
  console.log(`🔍 Tree-sitter availability check for "${language}" (normalized: "${lang}"): ${available} (Rust=${!!RustLanguage}, C=${!!CLanguage}, Go=${!!GoLanguage})`);
  
  return available;
}

/**
 * Get language grammar for Tree-sitter
 */
function getLanguageGrammar(language: string): Language | null {
  const lang = language.toLowerCase();
  
  if (lang === 'rust') return RustLanguage;
  if (lang === 'c') return CLanguage;
  if (lang === 'go') return GoLanguage;
  
  return null;
}

/**
 * Map Tree-sitter node types to CSS classes
 */
function getNodeStyling(nodeType: string, nodeText: string): string {
  // Enhanced mapping based on actual Tree-sitter node types
  const styleMap: Record<string, string> = {
    // Rust-specific nodes
    'let_declaration': 'keyword',
    'function_item': 'function',
    'method_call_expression': 'function',
    'scoped_identifier': 'function', // Box::new
    
    // C-specific nodes
    'function_definition': 'function',
    'function_declarator': 'function',
    
    // Go-specific nodes
    'func_literal': 'function',
    'function_declaration': 'function',
    'method_declaration': 'function',
    'short_var_declaration': 'keyword',
    'go_statement': 'keyword',
    'channel_type': 'type-keyword',
    'make_expression': 'function',
    'selector_expression': 'property',
    
    // Common nodes (shared between Rust, C, and Go)
    'call_expression': 'function',
    'field_expression': 'property',
    'type_identifier': 'type-keyword',
    'primitive_type': 'type-keyword',
    'identifier': 'variable',
    
    // Common nodes
    'string_literal': 'string',
    'raw_string_literal': 'string',
    'char_literal': 'string',
    'integer_literal': 'number',
    'float_literal': 'number',
    'boolean_literal': 'boolean',
    'line_comment': 'comment',
    'block_comment': 'comment',
    
    // Keywords (detected by text content)
    'fn': 'keyword',
    'let': 'keyword', 
    'mut': 'keyword',
    'const': 'keyword',
    'static': 'keyword',
    'struct': 'keyword',
    'enum': 'keyword',
    'impl': 'keyword',
    'trait': 'keyword',
    'pub': 'keyword',
    'use': 'keyword',
    'mod': 'keyword',
    'if': 'keyword',
    'else': 'keyword',
    'match': 'keyword',
    'for': 'keyword',
    'while': 'keyword',
    'loop': 'keyword',
    'return': 'keyword',
    'break': 'keyword',
    'continue': 'keyword'
  };
  
  // Check by node type first
  if (styleMap[nodeType]) {
    return styleMap[nodeType];
  }
  
  // Check by text content for keywords
  if (styleMap[nodeText]) {
    return styleMap[nodeText];
  }
  
  // Default styling based on node characteristics
  if (nodeType.includes('comment')) return 'comment';
  if (nodeType.includes('string')) return 'string';
  if (nodeType.includes('number') || nodeType.includes('literal')) return 'number';
  if (nodeType.includes('type')) return 'type-keyword';
  if (nodeType.includes('function') || nodeType.includes('call')) return 'function';
  if (nodeType.includes('keyword')) return 'keyword';
  
  // Punctuation and operators
  if (/^[{}()\[\];,.:]+$/.test(nodeText)) return 'text-gray-300';
  if (/^[+\-*/%=<>!&|^~]+$/.test(nodeText)) return 'operator';
  
  return 'variable';
}

/**
 * Extract tokens from Tree-sitter node recursively
 */
function extractTokensFromNode(
  node: any,
  sourceCode: string,
  codeHash: string,
  tokens: ASTToken[],
  lineOffset: number = 0
): void {
  // Skip nodes with no text content
  if (node.startIndex >= node.endIndex) {
    return;
  }
  
  const nodeText = sourceCode.slice(node.startIndex, node.endIndex);
  
  console.log(`🔍 Processing AST node: "${node.type}" with text: "${nodeText.slice(0, 50)}${nodeText.length > 50 ? '...' : ''}"`);
  
  // Define which node types should be treated as leaf tokens vs structural containers
  const structuralNodes = [
    'source_file', 'function_item', 'let_declaration', 'block', 
    'call_expression', 'arguments', 'field_initializer_list',
    'struct_expression', 'assignment_expression', 'field_initializer'
  ];
  
  const leafNodes = [
    'identifier', 'scoped_identifier', 'primitive_type', 'type_identifier',
    'string_literal', 'integer_literal', 'line_comment', 'block_comment',
    'field_identifier', 'macro_invocation'
  ];
  
  // If this is a structural node with children, recurse
  if (node.children.length > 0 && structuralNodes.includes(node.type)) {
    console.log(`📦 Recursing into structural node: ${node.type}`);
    for (const child of node.children) {
      extractTokensFromNode(child, sourceCode, codeHash, tokens, lineOffset);
    }
    return;
  }
  
  // If this is a leaf node OR a small compound node, create a token
  if (leafNodes.includes(node.type) || node.children.length === 0 || nodeText.length <= 30) {
    console.log(`🍃 Creating token for: ${node.type} = "${nodeText}"`);
    
    const token: ASTToken = {
      text: nodeText,
      type: node.type,
      startByte: node.startIndex,
      endByte: node.endIndex,
      startPosition: {
        row: node.startPosition.row + lineOffset,
        column: node.startPosition.column
      },
      endPosition: {
        row: node.endPosition.row + lineOffset,
        column: node.endPosition.column
      },
      nodeId: `${node.type}_${node.startIndex}_${node.endIndex}_${codeHash.substring(0, 8)}`,
      className: getNodeStyling(node.type, nodeText)
    };
    
    tokens.push(token);
  } else {
    // For other compound nodes with children, recurse
    console.log(`🌿 Recursing into compound node: ${node.type}`);
    for (const child of node.children) {
      extractTokensFromNode(child, sourceCode, codeHash, tokens, lineOffset);
    }
  }
}

/**
 * Parse code with real Tree-sitter
 */
export async function parseWithRealTreeSitter(
  code: string,
  language: string
): Promise<ParseResult> {
  const startTime = performance.now();
  
  try {
    // Check if Tree-sitter is initialized
    if (!parser) {
      const initialized = await initializeTreeSitter();
      if (!initialized) {
        throw new Error('Tree-sitter initialization failed');
      }
    }
    
    // Get language grammar
    const grammar = getLanguageGrammar(language);
    if (!grammar) {
      throw new Error(`No Tree-sitter grammar available for ${language}`);
    }
    
    // Set parser language
    parser!.setLanguage(grammar);
    
    // Parse the code
    const tree = parser!.parse(code);
    
    if (!tree) {
      throw new Error('Failed to parse code with Tree-sitter');
    }
    
    console.log(`🌳 Tree-sitter parsed ${language} code:`, tree.rootNode);
    
    // Generate code hash for deterministic node IDs
    const codeHash = generateSimpleHash(code);
    
    // Extract tokens from AST
    const allTokens: ASTToken[] = [];
    extractTokensFromNode(tree.rootNode, code, codeHash, allTokens);
    
    // Group tokens by line
    const lines = code.split('\n');
    const astLines: ASTLine[] = lines.map((lineText, index) => {
      const lineNumber = index + 1;
      
      // Find tokens that belong to this line
      const lineTokens = allTokens.filter(token => 
        token.startPosition.row === index
      ).sort((a, b) => a.startPosition.column - b.startPosition.column);
      
      // Fill gaps with whitespace/punctuation tokens
      const completeTokens = fillTokenGaps(lineText, lineTokens, index, codeHash);
      
      return {
        lineNumber,
        tokens: completeTokens,
        fullText: lineText
      };
    });
    
    const parseTime = performance.now() - startTime;
    
    return {
      success: true,
      lines: astLines,
      language,
      parseTime
      // tree omitted due to type incompatibility between TreeSitterTree and Tree
    };
    
  } catch (error) {
    const parseTime = performance.now() - startTime;
    console.error('❌ Tree-sitter parsing failed:', error);
    
    return {
      success: false,
      lines: [],
      language,
      parseTime,
      error: error instanceof Error ? error.message : 'Unknown parsing error'
    };
  }
}

/**
 * Fill gaps between AST tokens with whitespace and punctuation
 */
function fillTokenGaps(
  lineText: string, 
  astTokens: ASTToken[], 
  lineIndex: number,
  codeHash: string
): ASTToken[] {
  const completeTokens: ASTToken[] = [];
  let currentPos = 0;
  
  for (const token of astTokens) {
    const tokenStart = token.startPosition.column;
    
    // Add gap tokens before this AST token
    if (currentPos < tokenStart) {
      const gapText = lineText.slice(currentPos, tokenStart);
      if (gapText.trim()) {
        // Non-whitespace gap - add as unknown token
        completeTokens.push({
          text: gapText,
          type: 'unknown',
          startByte: currentPos,
          endByte: tokenStart,
          startPosition: { row: lineIndex, column: currentPos },
          endPosition: { row: lineIndex, column: tokenStart },
          nodeId: `gap_${currentPos}_${tokenStart}_${codeHash.substring(0, 8)}`,
          className: /^[{}()\[\];,.:]+$/.test(gapText) ? 'text-gray-300' : 'variable'
        });
      }
    }
    
    completeTokens.push(token);
    currentPos = token.endPosition.column;
  }
  
  // Add any remaining text at end of line
  if (currentPos < lineText.length) {
    const remainingText = lineText.slice(currentPos);
    if (remainingText.trim()) {
      completeTokens.push({
        text: remainingText,
        type: 'unknown',
        startByte: currentPos,
        endByte: lineText.length,
        startPosition: { row: lineIndex, column: currentPos },
        endPosition: { row: lineIndex, column: lineText.length },
        nodeId: `end_${currentPos}_${lineText.length}_${codeHash.substring(0, 8)}`,
        className: 'variable'
      });
    }
  }
  
  return completeTokens;
}

/**
 * Simple hash function for code content
 */
function generateSimpleHash(content: string): string {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Copy WASM files to public directory (development helper)
 */
export async function copyTreeSitterGrammars(): Promise<void> {
  console.log('📋 Tree-sitter WASM files should be copied to public/ directory:');
  console.log('  cp node_modules/tree-sitter-rust/tree-sitter-rust.wasm public/');
  console.log('  cp node_modules/tree-sitter-c/tree-sitter-c.wasm public/');
}